import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { GraphQlService } from '@shared/services/graphql-service';
import { catchError, exhaustMap, map, of, switchMap, tap } from 'rxjs';

import { ApolloNamespace } from '@shared/enums/apollo-namespaces';
import {
  companyStaffsRequest,
  companyStaffsFailed,
  companyStaffsSuccess,
  companyUserFailed,
  companyUserRequest,
  companyUserSuccess,
  globalRolesRequest,
  globalRolesFailed,
  globalRolesSuccess,
  viewCompanyUserRequest,
  viewCompanyUserFailed,
  viewCompanyUserSuccess,
  toggleCompanyUserStatusRequest,
  toggleCompanyUserStatusFailed,
  toggleCompanyUserStatusSuccess,
  viewGlobalRoleRequest,
  viewGlobalRoleFailed,
  viewGlobalRoleSuccess,
  deleteCompanyUserRequest,
  deleteCompanyUserFailed,
  deleteCompanyUserSuccess,
  listDesignationsRequest,
  listDesignationsFailed,
  listDesignationsSuccess,
  companyRegistrationLettersRequest,
  companyRegistrationLettersSuccess,
  companyRegistrationLettersFailed,
  viewCompanyLetterRequest,
  viewCompanyLetterSuccess,
  viewCompanyLetterFailed,
  resendInviteLinkRequest,
  resendInviteLinkSuccess,
  resendInviteLinkFailed,
} from './company-management.actions';
import { ListMyCompanyRegistrationLettersResponse } from '../response/company-registration-letters.response';
import { SaveCompanyUserResponse } from '../response/company-user.response';
import {
  DELETE_COMPANY_USER,
  GET_COMPANY_STAFFS,
  GET_GLOBAL_ROLES,
  LIST_OF_DESIGNATIONS,
  SAVE_COMPANY_USER,
  TOOGLE_ACTIVE_COMPANY_USER_STATUS,
  VIEW_COMPANY_USER,
  LIST_MY_COMPANY_REGISTRATION_LETTERS,
  VIEW_COMPANY_LETTER,
  RESEND_INVITE_LINK,
} from '../graphql/company-management.graphql';
import { ListMyCompanyUsersResponse } from '../response/company-staffs.response';
import { ListGlobalRolesResponse } from '../response/global-roles.response';
import { ViewCompanyUserByUuidResponse } from '../response/view-company-user.response';
import { ToggleCompanyUserStatusResponse } from '../response/toggle-company-user-status.response';
import { GET_ROLE_WITH_PERMISSIONS } from '@modules/role-management/role.graphql';
import { RoleWithPermissionsResponse } from '@modules/role-management/role';
import { DeleteCompanyUserResponse } from '../response/delete-company-user.response';

@Injectable({
  providedIn: 'root',
})
export class CompanyManagementEffects {
  private actions$ = inject(Actions);
  private graphQLService = inject(GraphQlService);

  $saveCompanyUserInformations = createEffect(() =>
    this.actions$.pipe(
      ofType(companyUserRequest),
      exhaustMap(({ request }) =>
        this.graphQLService
          .mutate<SaveCompanyUserResponse>({
            mutation: SAVE_COMPANY_USER,
            apolloNamespace: ApolloNamespace.uaa,
            variables: { companyUser: request },
          })
          .pipe(
            map((apolloResult) => {
              const payload = apolloResult.data?.saveCompanyUser;

              if (!payload?.status) {
                return companyUserFailed({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: payload?.message ?? 'Registration failed',
                    data: null,
                  },
                });
              }
              return companyUserSuccess({
                response: {
                  success: true,
                  statusCode: 200,
                  message: payload.message,
                  data: payload,
                },
              });
            }),
            catchError((err) =>
              of(
                companyUserFailed({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: err.message ?? 'Network error',
                    data: null,
                  },
                })
              )
            )
          )
      )
    )
  );

  $getCompanyStaffs = createEffect(() =>
    this.actions$.pipe(
      ofType(companyStaffsRequest),
      switchMap(({ request }) =>
        this.graphQLService
          .query<ListMyCompanyUsersResponse>({
            query: GET_COMPANY_STAFFS,
            apolloNamespace: ApolloNamespace.uaa,
            variables: { pageableParam: request }
          })
          .pipe(
            map((apolloResult) => {
              const payload = apolloResult?.data?.listMyCompanyUsers;
              if (!payload) {
                return companyStaffsFailed({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: null,
                    data: null,
                  },
                });
              }
              return companyStaffsSuccess({
                response: {
                  success: true,
                  statusCode: 200,
                  message: null,
                  data: payload,
                },
              });
            }),
            catchError((err) =>
              of(
                companyStaffsFailed({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: err.message ?? 'Network error',
                    data: null,
                  },
                })
              )
            )
          )
      )
    )
  );

  $globalRoles = createEffect(() =>
    this.actions$.pipe(
      ofType(globalRolesRequest),
      switchMap(() =>
        this.graphQLService
          .query<ListGlobalRolesResponse>({
            query: GET_GLOBAL_ROLES,
            apolloNamespace: ApolloNamespace.uaa,
          })
          .pipe(
            map((apolloResult) => {
              const payload = apolloResult?.data?.listGlobalRoles;
              if (!payload) {
                return globalRolesFailed({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: null,
                    data: null,
                  },
                });
              }
              return globalRolesSuccess({
                response: {
                  success: true,
                  statusCode: 200,
                  message: null,
                  data: payload,
                },
              });
            }),
            catchError((err) =>
              of(
                globalRolesFailed({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: err.message ?? 'Network error',
                    data: null,
                  },
                })
              )
            )
          )
      )
    )
  );

  $viewCompanyUser = createEffect(() =>
    this.actions$.pipe(
      ofType(viewCompanyUserRequest),
      switchMap(({ uuid }) =>
        this.graphQLService
          .query<ViewCompanyUserByUuidResponse>({
            query: VIEW_COMPANY_USER,
            apolloNamespace: ApolloNamespace.uaa,
            variables: { uuid: uuid },
          })
          .pipe(
            map((apolloResult) => {
              const payload = apolloResult?.data?.viewCompanyUserByUuid;
              if (!payload) {
                return viewCompanyUserFailed({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: null,
                    data: null,
                  },
                });
              }
              return viewCompanyUserSuccess({
                response: {
                  success: true,
                  statusCode: 200,
                  message: null,
                  data: payload,
                },
              });
            }),
            catchError((err) =>
              of(
                viewCompanyUserFailed({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: err.message ?? 'Network error',
                    data: null,
                  },
                })
              )
            )
          )
      )
    )
  );

  $viewGlobalRole = createEffect(() =>
    this.actions$.pipe(
      ofType(viewGlobalRoleRequest),
      switchMap(({ uuid }) =>
        this.graphQLService
          .query<RoleWithPermissionsResponse>({
            query: GET_ROLE_WITH_PERMISSIONS,
            apolloNamespace: ApolloNamespace.uaa,
            variables: { uuid: uuid },
          })
          .pipe(
            map((apolloResult) => {
              const payload = apolloResult?.data?.getRoleByUuid;
              const graphQlErrorMessage =
                (apolloResult as any)?.errors?.[0]?.message ??
                (apolloResult as any)?.error?.message ??
                null;

              if (!payload) {
                return viewGlobalRoleFailed({
                  error: {
                    success: false,
                    statusCode: 400,
                    message:
                      graphQlErrorMessage ?? 'Failed to fetch role permissions. Please try again.',
                    data: null,
                  },
                });
              }
              return viewGlobalRoleSuccess({
                response: {
                  success: true,
                  statusCode: 200,
                  message: null,
                  data: payload,
                },
              });
            }),
            catchError((err) =>
              of(
                viewGlobalRoleFailed({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: err.message ?? 'Network error',
                    data: null,
                  },
                })
              )
            )
          )
      )
    )
  );

  $toggleActiveStatus = createEffect(() =>
    this.actions$.pipe(
      ofType(toggleCompanyUserStatusRequest),
      exhaustMap(({ uuid }) =>
        this.graphQLService
          .mutate<ToggleCompanyUserStatusResponse>({
            mutation: TOOGLE_ACTIVE_COMPANY_USER_STATUS,
            apolloNamespace: ApolloNamespace.uaa,
            variables: { uuid: uuid },
          })
          .pipe(
            map((apolloResult) => {
              const payload = apolloResult.data?.toggleCompanyStaffActiveStatus;

              if (!payload?.status) {
                return toggleCompanyUserStatusFailed({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: payload?.message ?? 'Registration failed',
                    data: null,
                  },
                });
              }
              return toggleCompanyUserStatusSuccess({
                response: {
                  success: true,
                  statusCode: 200,
                  message: payload.message,
                  data: payload,
                },
              });
            }),
            catchError((err) =>
              of(
                toggleCompanyUserStatusFailed({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: err.message ?? 'Network error',
                    data: null,
                  },
                })
              )
            )
          )
      )
    )
  );

  $deleteCompanyUser = createEffect(() =>
    this.actions$.pipe(
      ofType(deleteCompanyUserRequest),
      exhaustMap(({ uuid }) =>
        this.graphQLService
          .mutate<DeleteCompanyUserResponse>({
            mutation: DELETE_COMPANY_USER,
            apolloNamespace: ApolloNamespace.uaa,
            variables: { uuid: uuid },
          })
          .pipe(
            map((apolloResult) => {
              const payload = apolloResult.data?.deleteCompanyUser;

              if (!payload?.status) {
                return deleteCompanyUserFailed({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: payload?.message ?? 'Registration failed',
                    data: null,
                  },
                });
              }
              return deleteCompanyUserSuccess({
                response: {
                  success: true,
                  statusCode: 200,
                  message: payload.message,
                  data: payload,
                },
              });
            }),
            catchError((err) =>
              of(
                deleteCompanyUserFailed({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: err.message ?? 'Network error',
                    data: null,
                  },
                })
              )
            )
          )
      )
    )
  );

  $listDesignations = createEffect(() =>
    this.actions$.pipe(
      ofType(listDesignationsRequest),
      switchMap(({ pageableParams, types }) =>
        this.graphQLService
          .query<any>({
            query: LIST_OF_DESIGNATIONS,
            apolloNamespace: ApolloNamespace.uaa,
            variables: { pageableParams, types: types ?? [] },
          })
          .pipe(
            map((apolloResult) => {
              const payload = apolloResult?.data?.listDesignationsPageable;
              if (!payload) {
                return listDesignationsFailed({
                  error: { success: false, statusCode: 400, message: 'Failed to load designations', data: null },
                });
              }
              return listDesignationsSuccess({
                response: { success: true, statusCode: 200, message: null, data: payload },
              });
            }),
            catchError((err) =>
              of(
                listDesignationsFailed({
                  error: { success: false, statusCode: 400, message: err.message ?? 'Network error', data: null },
                })
              )
            )
          )
      )
    )
  );

  $getCompanyRegistrationLetters = createEffect(() =>
    this.actions$.pipe(
      ofType(companyRegistrationLettersRequest),
      switchMap(({ request }) =>
        this.graphQLService
          .query<ListMyCompanyRegistrationLettersResponse>({
            query: LIST_MY_COMPANY_REGISTRATION_LETTERS,
            apolloNamespace: ApolloNamespace.registration,
            variables: { pageableParam: request }
          })
          .pipe(
            map((apolloResult) => {
              const payload = apolloResult?.data?.listMyCompanyRegistrationLetters;
              if (!payload) {
                return companyRegistrationLettersFailed({
                  error: { success: false, statusCode: 400, message: 'Failed to load letters', data: null },
                });
              }
              return companyRegistrationLettersSuccess({
                response: { success: true, statusCode: 200, message: null, data: payload },
              });
            }),
            catchError((err) =>
              of(
                companyRegistrationLettersFailed({
                  error: { success: false, statusCode: 400, message: err.message ?? 'Network error', data: null },
                })
              )
            )
          )
      )
    )
  );

  $viewCompanyLetter = createEffect(() =>
    this.actions$.pipe(
      ofType(viewCompanyLetterRequest),
      switchMap(({ uuid }) =>
        this.graphQLService
          .query<any>({
            query: VIEW_COMPANY_LETTER,
            apolloNamespace: ApolloNamespace.registration,
            variables: { uuid }
          })
          .pipe(
            map((apolloResult) => {
              const payload = apolloResult?.data?.getRegistrationLetterByUuid;
              if (!payload || !payload.data) {
                return viewCompanyLetterFailed({
                  error: { success: false, statusCode: 400, message: payload?.message ?? 'Failed to load letter', data: null },
                });
              }
              return viewCompanyLetterSuccess({
                response: { success: true, statusCode: 200, message: null, data: payload.data },
              });
            }),
            catchError((err) =>
              of(
                viewCompanyLetterFailed({
                  error: { success: false, statusCode: 400, message: err.message ?? 'Network error', data: null },
                })
              )
            )
          )
      )
    )
  );

  $resendInviteLink = createEffect(() =>
    this.actions$.pipe(
      ofType(resendInviteLinkRequest),
      exhaustMap(({ uuid }) =>
        this.graphQLService
          .mutate<any>({
            mutation: RESEND_INVITE_LINK,
            apolloNamespace: ApolloNamespace.uaa,
            variables: { uuid }
          })
          .pipe(
            map((apolloResult) => {
              const payload = apolloResult?.data?.resendInviteLink;
              if (!payload || !payload.status) {
                return resendInviteLinkFailed({
                  error: { success: false, statusCode: 400, message: payload?.message ?? 'Failed to resend invite link', data: null },
                });
              }
              return resendInviteLinkSuccess({
                response: { success: true, statusCode: 200, message: payload.message, data: payload.data },
              });
            }),
            catchError((err) =>
              of(
                resendInviteLinkFailed({
                  error: { success: false, statusCode: 400, message: err.message ?? 'Network error', data: null },
                })
              )
            )
          )
      )
    )
  );
}
